import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import SpanishDatePicker from "@/components/SpanishDatePicker";
import PlayerCombobox from "@/components/PlayerCombobox";
import { fmtDateEs, WEEKDAY_NAMES } from "@/lib/physio";

function genSlots(cfg) {
  const toMin = (s) => { const [h, m] = s.split(":").map(Number); return h * 60 + m; };
  const fmt = (n) => `${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
  const out = [];
  let t = toMin(cfg?.start_time || "19:00");
  const end = toMin(cfg?.end_time || "21:00");
  const dur = cfg?.slot_minutes || 15;
  while (t + dur <= end) { out.push({ start: fmt(t), end: fmt(t + dur) }); t += dur; }
  return out;
}

export default function PhysioAppointmentDialog({
  open, onOpenChange, teams, players, config, initial, onSaved,
}) {
  const isEdit = !!(initial && initial.id);
  const [form, setForm] = useState({
    date: "", start_time: "", team_id: "", player_id: "",
    responsible_person: "", reason: "", body_area: "", internal_notes: "", status: "booked",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      date: initial?.date || "",
      start_time: initial?.start_time || "",
      team_id: initial?.team_id_at_booking || initial?.team_id || "",
      player_id: initial?.player_id || "",
      responsible_person: initial?.responsible_person || config?.responsible_person || "",
      reason: initial?.reason || "",
      body_area: initial?.body_area || "",
      internal_notes: initial?.internal_notes || "",
      status: initial?.status === "confirmed" ? "confirmed" : "booked",
    });
  }, [open, initial, config]);

  const slots = useMemo(() => genSlots(config), [config]);
  const teamPlayers = useMemo(
    () => (form.team_id ? (players || []).filter((p) => p.team_id === form.team_id) : (players || [])),
    [players, form.team_id],
  );
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const endTime = useMemo(
    () => slots.find((s) => s.start === form.start_time)?.end || "",
    [slots, form.start_time],
  );
  const weekdayLabel = form.date ? WEEKDAY_NAMES[(new Date(form.date + "T00:00:00").getDay() + 6) % 7] : "";

  const save = async () => {
    if (!form.date || !form.start_time || !form.team_id || !form.player_id) {
      toast.error("Fecha, hora, equipo y jugador son obligatorios");
      return;
    }
    setSaving(true);
    try {
      const body = {
        date: form.date, start_time: form.start_time, team_id: form.team_id,
        player_id: form.player_id, responsible_person: form.responsible_person,
        reason: form.reason, body_area: form.body_area,
        internal_notes: form.internal_notes, status: form.status,
      };
      if (isEdit) await api.put(`/physio/appointments/${initial.id}`, body);
      else await api.post("/physio/appointments", body);
      toast.success(isEdit ? "Cita actualizada" : "Cita creada");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la cita");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="physio-appointment-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar cita de fisioterapia" : "Nueva cita de fisioterapia"}</DialogTitle>
          <DialogDescription>
            {form.date
              ? `${weekdayLabel.charAt(0).toUpperCase() + weekdayLabel.slice(1)} · ${fmtDateEs(form.date)}${form.start_time ? ` · ${form.start_time}${endTime ? `–${endTime}` : ""}` : ""}`
              : "Selecciona fecha y hora del turno (martes y viernes, 19:00–21:00)."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fecha *</Label>
              <SpanishDatePicker value={form.date} onChange={set("date")} testId="physio-form-date" />
            </div>
            <div>
              <Label>Hora *</Label>
              <Select value={form.start_time} onValueChange={set("start_time")}>
                <SelectTrigger data-testid="physio-form-time"><SelectValue placeholder="Turno" /></SelectTrigger>
                <SelectContent>
                  {slots.map((s) => (
                    <SelectItem key={s.start} value={s.start} data-testid={`physio-form-time-${s.start}`}>
                      {s.start}–{s.end}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Equipo *</Label>
              <Select value={form.team_id} onValueChange={(v) => setForm((f) => ({ ...f, team_id: v, player_id: "" }))}>
                <SelectTrigger data-testid="physio-form-team"><SelectValue placeholder="Equipo" /></SelectTrigger>
                <SelectContent>
                  {(teams || []).map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`physio-form-team-${t.id}`}>
                      {t.name}{t.category ? ` · ${t.category}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Jugador *</Label>
              <PlayerCombobox
                players={teamPlayers}
                value={form.player_id}
                onChange={set("player_id")}
                testId="physio-form-player"
                className="w-full"
                placeholder={form.team_id ? "Buscar jugador…" : "Todos los equipos"}
              />
            </div>
          </div>

          <div>
            <Label>Persona responsable</Label>
            <Input value={form.responsible_person} onChange={(e) => set("responsible_person")(e.target.value)}
              placeholder="Fisioterapeuta" data-testid="physio-form-responsible" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Motivo breve</Label>
              <Input value={form.reason} onChange={(e) => set("reason")(e.target.value)}
                placeholder="Ej. sobrecarga" data-testid="physio-form-reason" />
            </div>
            <div>
              <Label>Zona corporal</Label>
              <Input value={form.body_area} onChange={(e) => set("body_area")(e.target.value)}
                placeholder="Ej. gemelo" data-testid="physio-form-body-area" />
            </div>
          </div>
          <div>
            <Label>Observaciones internas</Label>
            <Textarea value={form.internal_notes} onChange={(e) => set("internal_notes")(e.target.value)}
              rows={2} placeholder="Solo visible para el Director Deportivo" data-testid="physio-form-notes" />
          </div>
          <div>
            <Label>Estado</Label>
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="physio-form-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="booked">Reservada</SelectItem>
                <SelectItem value="confirmed">Confirmada</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="physio-form-cancel">Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="physio-form-save"
            className="bg-purple-700 hover:bg-purple-800">
            {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear cita"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
